import { Link } from "react-router-dom";
import { Lock, Check, Radio, CalendarClock, Ban, Sparkles, Star } from "lucide-react";
import { TeamMark } from "@/components/Bits";
import AdvancedQuestions from "@/components/AdvancedQuestions";
import CopyPicksButton from "@/components/CopyPicksButton";
import { StatusPill, winnerStatus } from "@/components/StatusPill";
import { signed } from "@/lib/share";
import { fmtTime, fmtDateTime, STATUS_LABEL } from "@/lib/format";

const statusTone = {
  scheduled: "text-slate-300 bg-white/5 border-white/10",
  live: "text-emerald-300 bg-emerald-400/10 border-emerald-400/30",
  final: "text-white bg-white/10 border-white/15",
  postponed: "text-amber-300 bg-amber-400/10 border-amber-400/30",
  cancelled: "text-red-300 bg-red-400/10 border-red-400/30",
};

function TeamButton({ game, side, onPick, saving }) {
  const team = game[side];
  const selected = game.my_pick === side;
  const won = game.status === "final" && game.winner === side;
  const disabled = game.locked || saving || game.excluded;
  const score = side === "home" ? game.home_score : game.away_score;
  const base = "group relative flex w-full items-center gap-3 rounded-xl border p-3 text-left transition-[transform,background-color,border-color] duration-150";
  const tone = selected
    ? "border-[#B7F52A] bg-[#B7F52A] text-[#05070D] shadow-lg shadow-[#B7F52A]/15"
    : "border-white/10 bg-[#0B1020] text-white hover:border-white/25 hover:bg-[#1A2542]";
  return (
    <button
      type="button"
      data-testid={`match-card-pick-${side}-${game.id}`}
      disabled={disabled}
      aria-pressed={selected}
      onClick={() => onPick && onPick(game, side)}
      className={`${base} ${tone} ${disabled ? "cursor-not-allowed" : "active:scale-[0.98]"} ${game.locked && !selected ? "opacity-60" : ""}`}
    >
      <TeamMark team={team} size={40} />
      <span className="min-w-0 flex-1">
        <span className={`block text-[10px] font-bold uppercase tracking-wider ${selected ? "text-[#05070D]/70" : "text-slate-500"}`}>
          {side === "home" ? "Domicile" : "Visiteurs"}
        </span>
        <span className="block truncate text-sm font-bold">{team.name}</span>
      </span>
      {score != null && (
        <span className={`font-mono-num text-lg font-bold ${won && !selected ? "text-[#B7F52A]" : ""}`}>{score}</span>
      )}
      {selected && <Check size={18} className="shrink-0" />}
    </button>
  );
}

function Footer({ game }) {
  return (
    <span className="flex flex-wrap items-center gap-2">
      <StatusPill s={winnerStatus(game)} testId={`winner-status-${game.id}`} />
      <FooterText game={game} />
    </span>
  );
}

function FooterText({ game }) {
  if (game.status === "cancelled")
    return <span className="flex items-center gap-1.5 text-red-300"><Ban size={14} /> Match annulé : aucun point, aucune erreur</span>;
  if (game.status === "final") {
    if (!game.my_pick) return <span className="text-slate-400">Terminé · aucune prédiction</span>;
    const ok = game.my_pick === game.winner;
    return (
      <span className="flex flex-wrap items-center gap-2">
        <span className={ok ? "font-semibold text-[#B7F52A]" : "text-slate-400"}>{ok ? "Bonne prédiction" : "Prédiction manquée"}</span>
        {game.points != null && <span data-testid={`game-points-${game.id}`} className={`font-mono-num font-bold ${game.points >= 0 ? "text-[#B7F52A]" : "text-red-300"}`}>{signed(game.points)} pt{Math.abs(game.points) > 1 ? "s" : ""} au total</span>}
        {game.streak_bonus && <span className="flex items-center gap-1 rounded-full bg-[#B7F52A]/10 px-2 py-0.5 text-[11px] font-bold text-[#B7F52A]"><Sparkles size={11} /> Bonus série</span>}
      </span>
    );
  }
  if (game.locked)
    return <span data-testid={`prediction-lock-badge-${game.id}`} className="flex items-center gap-1.5 text-slate-300"><Lock size={14} /> Verrouillé {game.my_pick ? "· prédiction enregistrée" : "· aucune prédiction"}</span>;
  if (game.my_pick)
    return <span data-testid={`prediction-saved-${game.id}`} className="flex items-center gap-1.5 font-semibold text-[#B7F52A]"><Check size={14} /> Prédiction enregistrée · touche à nouveau l'équipe pour la retirer</span>;
  return <span className="text-slate-400">Choisissez le vainqueur</span>;
}

export const liveLabel = (g) => {
  if (g.status !== "live") return null;
  const t = (g.status_text || "").toLowerCase();
  if (t.includes("half")) return "Mi-temps";
  if (!g.period) return null;
  const q = g.period > 4 ? `Prol. ${g.period - 4}` : `Q${g.period}`;
  return g.clock && !/^\d{4}-/.test(g.clock) ? `${q} · ${g.clock}` : q;
};

const countdown = (iso) => {
  const ms = new Date(iso) - new Date();
  if (ms <= 0) return null;
  const h = Math.floor(ms / 3600000), m = Math.floor((ms % 3600000) / 60000);
  return h >= 48 ? `${Math.floor(h / 24)} j` : h ? `${h} h ${String(m).padStart(2, "0")}` : `${m} min`;
};

function Stakes({ game, rule }) {
  if (!rule || game.locked) return null;
  const qs = (game.questions || []).filter((q) => q.status !== "void");
  const answered = qs.filter((q) => q.my_answer);
  const gain = (game.my_pick ? rule.correct_points : 0) + answered.reduce((s, q) => s + q.points_win, 0);
  const risk = (game.my_pick ? rule.wrong_points : 0) + answered.reduce((s, q) => s + q.points_lose, 0);
  const left = countdown(game.start_time);
  return (
    <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-[11px]" data-testid={`game-stakes-${game.id}`}>
      <span className="text-slate-400">Vainqueur : <b className="text-[#B7F52A]">{signed(rule.correct_points)}</b> / <b className={rule.wrong_points < 0 ? "text-red-300" : "text-slate-300"}>{signed(rule.wrong_points)}</b>{left && <> · verrouillage dans <b className="text-white">{left}</b></>}</span>
      {(game.my_pick || answered.length > 0) && <span className="font-semibold text-slate-200" data-testid={`game-max-${game.id}`}>Gain max : <b className="text-[#B7F52A]">{signed(gain)}</b> · Risque max : <b className="text-red-300">{signed(risk)}</b></span>}
    </div>
  );
}

export default function GameCard({ game, tz, onPick, saving, showDate, extra, setGames, rule }) {
  const Icon = game.status === "live" ? Radio : game.status === "postponed" ? CalendarClock : null;
  const live = liveLabel(game);
  return (
    <article data-testid={`game-card-${game.id}`} className={`mpl-card mpl-game mpl-rise p-4 ${game.excluded ? "opacity-50" : ""}`}>
      <div className="mpl-photo mpl-photo-court" aria-hidden="true" />
      <div className="mb-3 flex items-center justify-between gap-2">
        <Link to={`/matchs/${game.id}`} data-testid={`game-detail-link-${game.id}`} className="font-mono-num rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-xs text-slate-200 hover:border-white/25">
          {showDate ? fmtDateTime(game.start_time, tz) : fmtTime(game.start_time, tz)}
        </Link>
        {game.featured && <span data-testid={`game-featured-${game.id}`} className="flex items-center gap-1 rounded-full bg-[#B7F52A] px-2.5 py-1 text-[11px] font-bold uppercase text-[#101310]"><Star size={11} fill="currentColor" /> Vedette{game.featured_points ? ` +${game.featured_points}` : ""}</span>}
        <span data-testid={`game-status-${game.id}`} className={`flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide ${statusTone[game.status]}`}>
          {Icon && <Icon size={12} className={game.status === "live" ? "animate-pulse" : ""} />} {STATUS_LABEL[game.status]}
          {live && <span data-testid={`game-live-clock-${game.id}`} className="font-mono-num normal-case tracking-normal">· {live}</span>}
        </span>
      </div>
      <div className="grid gap-2">
        <TeamButton game={game} side="away" onPick={onPick} saving={saving} />
        <TeamButton game={game} side="home" onPick={onPick} saving={saving} />
      </div>
      <Stakes game={game} rule={rule} />
      <div className="mt-3 flex items-center justify-between gap-2 text-xs">
        <Footer game={game} />
        {extra}
      </div>
      {setGames && <AdvancedQuestions game={game} setGames={setGames} />}
      {!game.locked && game.league_id && (game.my_pick || (game.questions || []).some((q) => q.my_answer)) && (
        <CopyPicksButton leagueId={game.league_id} gameIds={[game.id]} label="Copier ce prono dans mes autres ligues" testId={`copy-game-${game.id}`} className="mt-3 w-full justify-center" />
      )}
    </article>
  );
}
